import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import axios from 'axios';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { AuthenticateSelect } from '../../../../auth/select';
import { TextInput } from '../../../../form/textInput';
import { Select } from '../../../../form/select';
import { Textarea } from '../../../../form/textarea';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';
import { updateNodeValue } from '../../../../../services/method';
import { operationType, selectDataType } from '../../../../../services/types';
import { webURL } from '../../../../../services/API';
import { Main, Styled } from './style';

type TeamsProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const requestOptions: selectDataType[] = [
   { id: 'send_message', name: 'Send Message', refId: 'send_message', isActive: true },
   { id: 'get_messages', name: 'Get Messages', refId: 'get_messages' },
   { id: 'search_messages', name: 'Search Messages', refId: 'search_messages' },
   // { id: 'reply_message', name: 'Reply Message', refId: 'reply_message' },
   // { id: 'update_message', name: 'Update Message', refId: 'update_message' },
   // { id: 'soft_delete_message', name: 'Soft Delete Message', refId: 'soft_delete_message' },
   { id: 'list_teams', name: 'List Teams', refId: 'list_teams' },
   { id: 'list_channels', name: 'List Channels', refId: 'list_channels' },
   { id: 'list_chats', name: 'List Chats', refId: 'list_chats' },
   // { id: 'list_members', name: 'List Members', refId: 'list_members' },
   { id: 'list_files', name: 'List Files', refId: 'list_files' },
   { id: 'create_text_file', name: 'Create Text File', refId: 'create_text_file' },
   // { id: 'delete_file', name: 'Delete File', refId: 'delete_file' },
   { id: 'agent_decide', name: 'Let AI Agent Decide', refId: 'agent_decide' },
];

const scopeOptions: selectDataType[] = [
   { id: 'channel', name: 'Channel', refId: 'channel', isActive: true },
   { id: 'chat', name: 'Chat', refId: 'chat' },
];

function toSelectItems(items: any[] = [], activeId?: string) {
   return items.map((item, index) => ({
      id: item.id,
      refId: item.id,
      name: item.name || item.displayName || item.topic || item.id || `Item ${index + 1}`,
      isActive: activeId ? item.id === activeId : index === 0,
   }));
}

function getActiveId(items?: any[]) {
   return Array.isArray(items) ? (items.find(item => item.isActive)?.id || items[0]?.id || '') : '';
}

function cacheBust() {
   return `_=${Date.now()}`;
}

export function Teams({ id, data, setChildMinimize, setChildSettings }: TeamsProps) {
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [isLoading, setIsLoading] = React.useState(false);
   const [loadError, setLoadError] = React.useState('');
   const connectionId = data.value?.connectionId || data.value?.connection?._id || data.value?.connection?.id || '';
   const request = data.value?.request || 'send_message';
   const scopeType = data.value?.scopeType || 'channel';

   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   }, [setChildMinimize, setChildSettings]);

   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, {
            request: 'send_message',
            scopeType: 'channel',
            teams: [],
            channels: [],
            chats: [],
            limit: 20,
         });
      }
   }, [data.value, id, setNodes]);

   const handleAuthenticate = async (type: 'add' | 'remove', connection?: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, {
            connection,
            connectionId: connection?.id || null,
            teams: [],
            channels: [],
            chats: [],
         });
      } else {
         updateNodeValue(id, setNodes, { connection: null, connectionId: null, teams: [], channels: [], chats: [] });
      }
   };

   const handleChangeInput = (e: any, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: typeof e === 'string' ? e : e.target.value });
   };

   const handleSelectRequest = (selectId: any) => {
      updateNodeValue(id, setNodes, { request: selectId });
   };

   const handleSelectScope = (selectId: any) => {
      updateNodeValue(id, setNodes, { scopeType: selectId });
   };

   const handleSelectCollection = (key: 'teams' | 'channels' | 'chats', selectId: any) => {
      const next = (data.value?.[key] || []).map((item: any) => ({ ...item, isActive: item.id === selectId }));
      const selected = next.find((item: any) => item.isActive);
      const update: any = { [key]: next };
      if (key === 'teams') update.teamId = selected?.id || '';
      if (key === 'channels') update.channelId = selected?.id || '';
      if (key === 'chats') update.chatId = selected?.id || '';
      updateNodeValue(id, setNodes, update);
   };

   const fetchTeams = async () => {
      if (!connectionId) return;
      setIsLoading(true);
      setLoadError('');
      try {
         const response = await axios.get(`${webURL}/teams/teams?connectionId=${connectionId}&${cacheBust()}`);
         console.log(response)

         const teams = toSelectItems(response.data?.items || [], data.value?.teamId);
         updateNodeValue(id, setNodes, { teams, teamId: getActiveId(teams), channels: [] });
      } catch (err: any) {
         setLoadError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load Teams');
      } finally {
         setIsLoading(false);
      }
   };

   const fetchChannels = async () => {
      if (!connectionId) return;
      const teamId = data.value?.teamId || getActiveId(data.value?.teams);
      if (!teamId) return;
      setIsLoading(true);
      setLoadError('');
      try {
         const response = await axios.get(`${webURL}/teams/channels?connectionId=${connectionId}&teamId=${encodeURIComponent(teamId)}&${cacheBust()}`);
         const channels = toSelectItems(response.data?.items || [], data.value?.channelId);
         updateNodeValue(id, setNodes, { channels, channelId: getActiveId(channels) });
      } catch (err: any) {
         setLoadError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load channels');
      } finally {
         setIsLoading(false);
      }
   };

   const fetchChats = async () => {
      if (!connectionId) return;
      setIsLoading(true);
      setLoadError('');
      try {
         const response = await axios.get(`${webURL}/teams/chats?connectionId=${connectionId}&${cacheBust()}`);
         const chats = toSelectItems((response.data?.items || []).map((chat: any) => ({
            id: chat.id,
            name: chat.topic || `${chat.chatType || 'Chat'} ${chat.id?.slice?.(0, 8) || ''}`,
         })), data.value?.chatId);
         updateNodeValue(id, setNodes, { chats, chatId: getActiveId(chats) });
      } catch (err: any) {
         setLoadError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load chats');
      } finally {
         setIsLoading(false);
      }
   };

   const requestItems = requestOptions.map(option => ({ ...option, isActive: option.id === request }));
   const currentScopeItems = scopeOptions.map(option => ({ ...option, isActive: option.id === scopeType }));
   const showScope = ['send_message', 'get_messages', 'reply_message', 'update_message', 'soft_delete_message', 'list_members', 'search_messages'].includes(request);
   const showChannelScope = ['list_channels', 'list_files', 'create_text_file'].includes(request) || (showScope && scopeType === 'channel');
   const showChatScope = scopeType === 'chat' && showScope;
   const showLoadTeams = showChannelScope;
   const showLoadChannels = showChannelScope && request !== 'list_channels';
   const showLoadChats = showChatScope;
   const showCollectionActions = showLoadTeams || showLoadChannels || showLoadChats;
   const needsContent = ['send_message', 'reply_message', 'update_message', 'create_text_file'].includes(request);
   const needsMessageId = ['reply_message', 'update_message', 'soft_delete_message'].includes(request);
   const isWarning = !connectionId;

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: '460px' }}
         ><Styled>
               <AuthenticateSelect
                  type="microsoft"
                  defaultId={connectionId}
                  defaultConnection={data.value?.connection}
                  handleAuthenticate={handleAuthenticate}
                  background={data.background}
               />
               {showCollectionActions ? <div className="action">
                  {showLoadTeams ? <button className="btn1 withBorder" onClick={fetchTeams} disabled={!connectionId || isLoading}>Load teams</button> : null}
                  {showLoadChannels ? <button className="btn1 withBorder" onClick={fetchChannels} disabled={!connectionId || isLoading || !getActiveId(data.value?.teams)}>Load channels</button> : null}
                  {showLoadChats ? <button className="btn1 withBorder" onClick={fetchChats} disabled={!connectionId || isLoading}>Load chats</button> : null}
               </div> : null}
               {loadError ? <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 8px' }}>{loadError}</p> : null}
               <Select title="Request" data={requestItems} handleSelectItem={handleSelectRequest} isSmall />
               {showScope ? <Select title="Scope" data={currentScopeItems} handleSelectItem={handleSelectScope} isSmall /> : null}
               {showChannelScope ? <>
                  <Select title="Team" data={data.value?.teams || []} handleSelectItem={(itemId) => handleSelectCollection('teams', itemId)} isSmall emptyMsg="Load teams" />
                  {request !== 'list_channels' ? <Select title="Channel" data={data.value?.channels || []} handleSelectItem={(itemId) => handleSelectCollection('channels', itemId)} isSmall emptyMsg="Load channels" /> : null}
               </> : null}
               {showChatScope ? <Select title="Chat" data={data.value?.chats || []} handleSelectItem={(itemId) => handleSelectCollection('chats', itemId)} isSmall emptyMsg="Load chats" /> : null}
               {['get_messages', 'list_chats', 'list_files', 'search_messages'].includes(request) ? <TextInput keyRef="limit" value={data.value?.limit} title="Limit" placeholder="20" type="number" handleChange={handleChangeInput} /> : null}
               {request === 'search_messages' ? <>
                  <TextInput keyRef="query" value={data.value?.query} title="Query" placeholder="Search query" handleChange={handleChangeInput} />
               </> : null}
               {needsMessageId ? <TextInput keyRef="messageId" value={data.value?.messageId} title="Message ID" placeholder="message id" handleChange={handleChangeInput} /> : null}
               {request === 'create_text_file' ? <TextInput keyRef="filename" value={data.value?.filename} title="Filename" placeholder="notes.txt" handleChange={handleChangeInput} /> : null}
               {request === 'delete_file' ? <>
                  <TextInput keyRef="driveId" value={data.value?.driveId} title="Drive ID" placeholder="drive id" handleChange={handleChangeInput} />
                  <TextInput keyRef="itemId" value={data.value?.itemId} title="Item ID" placeholder="file item id" handleChange={handleChangeInput} />
               </> : null}
               {needsContent ? <Textarea keyRef="content" value={data.value?.content} placeholder={request === 'create_text_file' ? 'File content' : 'Message content'} handleChange={handleChangeInput} /> : null}
            </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}
